import React, { useState, useEffect, useRef } from "react";
import { useNavigate, NavLink } from "react-router";
import { burgerMenu, close, notification } from "../../assets/icons";
import SignolUtils from "@src/utils/signolUtils";
import { useAuthContext, useHeaderCalloutContext } from "../../context";
import { NotificationBadge, ImageWithRetry, DialogBubble } from "../atoms";

const AppHeader = (props) => {
  const [displayProfileMenu, setDisplayProfileMenu] = useState(false);
  const {
    logoSrc,
    signolLogoSrc,
    avatarInitials,
    onSwitchRole,
    onImpersonatedSession,
    onLogout,
    showMenu,
    containerWidth = "1120px",
    navigationMenuOpen,
    toggleNavigationMenu,
  } = props;
  const auth = useAuthContext();
  const { headerCallout } = useHeaderCalloutContext();
  const {
    activeRoleRoutePrefix,
    isActiveRoleOperator,
    isActiveRoleManager,
    isActiveRoleAdmin,
  } = auth;
  const hasNotificationsEnabled = isActiveRoleOperator;
  const customerLogo = logoSrc ? (
    <ImageWithRetry className="h-8 w-auto" src={logoSrc} alt="logo" />
  ) : (
    ""
  );
  const signolLogo = signolLogoSrc ? (
    <ImageWithRetry
      className="h-8 w-auto"
      src={signolLogoSrc}
      alt="Signol logo"
    />
  ) : (
    ""
  );
  const navigateTo = useNavigate();

  const profileMenuRef = useRef(null);
  const profileMenuButtonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        profileMenuButtonRef.current &&
        !profileMenuButtonRef.current.contains(event.target) &&
        displayProfileMenu
      ) {
        setDisplayProfileMenu(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuRef, profileMenuButtonRef, displayProfileMenu]);

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm w-full">
      <div
        className="w-full mx-auto px-4 md:px-8"
        style={{ maxWidth: containerWidth }}
      >
        <div className="flex justify-between h-16">
          <div className="flex">
            {showMenu && (
              <div className="-ml-2 mr-2 flex items-center">
                <button
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out cursor-pointer"
                  aria-label="Main menu"
                  onClick={() => {
                    window.scrollTo({ top: 0 });
                    toggleNavigationMenu();
                  }}
                >
                  {!navigationMenuOpen && (
                    <div className="w-8">{burgerMenu()}</div>
                  )}
                  {navigationMenuOpen && (
                    <div>
                      <div className="block">
                        <div className="w-8">{close()}</div>
                      </div>
                    </div>
                  )}
                </button>
              </div>
            )}
            <div
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => {
                if (
                  ![
                    "/dashboard",
                    `/dashboard/${activeRoleRoutePrefix}/overview`,
                  ].includes(window.location.pathname)
                ) {
                  navigateTo(`/dashboard/${activeRoleRoutePrefix}/overview`);
                }
              }}
            >
              {isActiveRoleAdmin || isActiveRoleManager
                ? signolLogo
                : customerLogo}
            </div>
          </div>
          <div className="flex items-center">
            <div className="ml-4 flex-shrink-0 flex items-center">
              {isActiveRoleManager && customerLogo}
              {hasNotificationsEnabled && (
                <NotificationBadge
                  isActive={false}
                  className="w-10 p-1 border-2 border-transparent text-gray-400 hover:text-primary-400 focus:outline-none focus:text-primary-400 focus:bg-gray-100"
                />
              )}
              <div className="ml-3 relative z-10">
                <div>
                  <DialogBubble
                    target={
                      <button
                        className="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:border-primary-400 hover:border-primary-400 transition duration-150 ease-in-out cursor-pointer"
                        ref={profileMenuButtonRef}
                        id="user-menu"
                        aria-label="User menu"
                        aria-haspopup="true"
                        onClick={() =>
                          setDisplayProfileMenu(!displayProfileMenu)
                        }
                      >
                        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500">
                          <span className="text-sm font-medium leading-none text-white">
                            {avatarInitials?.toUpperCase()}
                          </span>
                        </span>
                      </button>
                    }
                    direction={headerCallout?.direction || "down-left"}
                    visible={!!headerCallout}
                    onDismiss={headerCallout?.onDismiss}
                    bgColor={headerCallout?.bgColor}
                  >
                    {headerCallout?.content}
                  </DialogBubble>
                </div>
                {displayProfileMenu && (
                  <div
                    ref={profileMenuRef}
                    className="origin-top-right absolute right-0 mt-2 w-36 rounded-md shadow-xl"
                  >
                    <div
                      className="py-1 rounded-md bg-white shadow-xs"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu"
                    >
                      <NavLink
                        className="block cursor-pointer px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                        to={`/dashboard/${activeRoleRoutePrefix}/profile`}
                        {...SignolUtils.makeTrackableDataAttributes({
                          type: "MENU_ITEM",
                          toURL: `/dashboard/${activeRoleRoutePrefix}/profile`,
                        })}
                      >
                        Profile
                      </NavLink>
                      <div className="hidden md:block">
                        {!!onSwitchRole && (
                          <div
                            className="block cursor-pointer px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                            role="menuitem"
                            onClick={() => onSwitchRole(null)}
                          >
                            Switch Role
                          </div>
                        )}
                      </div>
                      <div className="hidden md:block">
                        {!!onImpersonatedSession && (
                          <div
                            className="block cursor-pointer px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                            role="menuitem"
                            onClick={() => onImpersonatedSession()}
                          >
                            Logout from User
                          </div>
                        )}
                      </div>
                      {!onImpersonatedSession && (
                        <div
                          onClick={() => onLogout()}
                          className="block cursor-pointer px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                          role="menuitem"
                        >
                          Logout
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AppHeader;

export const AppHeaderControls = {
  logoSrc: {
    controlType: "Text",
    description: "SRC of customer logo",
  },
  signolLogoSrc: {
    controlType: "Text",
    description: "SRC of signol logo",
  },
  avatarInitials: {
    controlType: "Text",
    description: "User initials to display in Avatar",
  },
  // onSwitchRole: {},
  // onImpersonatedSession: {},
  // onLogout: {},
  hasNewNotifications: {
    controlType: "boolean",
    description: "Does the user have unread notifications",
  },
  showMenu: {
    controlType: "boolean",
    description: "Is the burger menu to be displayed",
  },
  containerWidth: {
    controlType: "Text",
    description: "Overide for the width of the content defaults to 1120px",
  },
  navigationMenuOpen: {
    controlType: "boolean",
    description: "Is the navigation menu open",
  },
};
